// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Tooltip from "@insite/mobius/Tooltip";
import setCustomerNumber from "@store/Framework/Store/Greif/Pages/ContactUs/Handlers/SetCustomerNumber";
import { topicToHideFieldsArray } from "@store/Overrides/Widgets/ContactUs/ContactForm";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    tooltipText = "tooltipText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.tooltipText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    customerNumber: state.greif.pages.contactUs.customerNumber,
    topic: state.components.contactUsForm.fieldValues["topic"],
});

const mapDispatchToProps = {
    setCustomerNumber,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerNumberStyles {
    customerNumberStylesTextField?: TextFieldPresentationProps;
}

export const customerNumberStyles: CustomerNumberStyles = {};

const CustomerNumber: React.FC<Props> = ({ fields, customerNumber, setCustomerNumber, topic }) => {
    const { label, hintText, tooltipText, isRequired } = fields;
    const contactFormContext = useContext(ContactFormContext);
    const [customerNumberErrorMessage, setCustomerNumberErrorMessage] = React.useState("");
    const [hasValidated, setHasValidated] = React.useState(false);
    const styles = React.useMemo(() => {
        const showField = !topicToHideFieldsArray.includes(topic || "");
        return showField
            ? customerNumberStyles
            : mergeProps<CustomerNumberStyles>(customerNumberStyles, {
                  customerNumberStylesTextField: {
                      cssOverrides: {
                          formField: css`
                              display: none;
                          `,
                      },
                  },
              });
    }, [topic]);

    React.useEffect(() => {
        contactFormContext.validators.customerNumber = validateCustomerNumbers;
    }, [customerNumber]);

    React.useEffect(() => {
        if (hasValidated) {
            validateCustomerNumbers();
        }
    }, [customerNumber, hasValidated]);

    React.useEffect(() => {
        contactFormContext.validators.customerNumber = validateCustomerNumbers;
    }, [isRequired]);

    const validateCustomerNumbers = (value?: string) => {
        setHasValidated(true);
        const effectiveValue = value ?? customerNumber ?? "";
        const errorMessage = isRequired && !effectiveValue ? (siteMessage("Field_Required", label) as string) : "";
        setCustomerNumberErrorMessage(errorMessage);
        return !errorMessage;
    };

    const customerNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerNumber({ value: event.target.value });
        if (hasValidated) {
            validateCustomerNumbers(event.target.value);
        }
    };

    return (
        <TextField
            {...styles.customerNumberStylesTextField}
            required={isRequired}
            label={
                <>
                    {label}
                    {!!tooltipText?.trim() && <Tooltip text={tooltipText} />}
                </>
            }
            placeholder={hintText}
            value={customerNumber || ""}
            error={customerNumberErrorMessage}
            onChange={customerNumberChangeHandler}
            type="number"
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerNumber),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        displayName: "Customer Number",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Customer Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Customer Number",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.tooltipText,
                displayName: "Tooltip Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "You can find your Greif Ship to Customer Number on Greif Invoices",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
